import React, {
  useCallback, useEffect, useRef, useState,
} from "react";
import { useParams } from "react-router-dom";
import { getAuthenticatedHttpClient } from "@edx/frontend-platform/auth";
import { getConfig } from "@edx/frontend-platform";
import { Alert, Spinner } from "@openedx/paragon";
import { injectIntl, intlShape } from "@edx/frontend-platform/i18n";
import messages from "./messages";

import "./StaticTab.scss";

export const STATIC_TAB_RESIZE_MESSAGE_TYPE = "staticTab.resize";
export const STATIC_TAB_MIN_HEIGHT = 400;

const getElementHeight = (doc) => {
  if (!doc) {
    return STATIC_TAB_MIN_HEIGHT;
  }

  const { body, documentElement } = doc;
  return Math.max(
    body?.scrollHeight || 0,
    body?.offsetHeight || 0,
    documentElement?.clientHeight || 0,
    documentElement?.scrollHeight || 0,
    documentElement?.offsetHeight || 0,
    STATIC_TAB_MIN_HEIGHT,
  );
};

const escapeHtmlAttribute = (value) => String(value)
  .replace(/&/g, "&amp;")
  .replace(/"/g, "&quot;")
  .replace(/</g, "&lt;");

const removeTrailingSlash = (value) => String(value || "").replace(/\/$/, "");

const isRewritableUrl = (url) => {
  const trimmedUrl = String(url || "").trim();

  return trimmedUrl
    && !trimmedUrl.startsWith("#")
    && !/^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(trimmedUrl);
};

const toAbsoluteUrl = (url, baseUrl) => {
  if (!isRewritableUrl(url)) {
    return url;
  }

  try {
    return new URL(url, baseUrl).toString();
  } catch (parseError) {
    return url;
  }
};

const rewriteSrcSet = (srcSet, baseUrl) => String(srcSet || "")
  .split(",")
  .map((candidate) => {
    const trimmedCandidate = candidate.trim();
    const [url, ...descriptors] = trimmedCandidate.split(/\s+/);

    if (!url) {
      return candidate;
    }

    return [toAbsoluteUrl(url, baseUrl), ...descriptors].join(" ");
  })
  .join(", ");

const rewriteCssUrls = (cssText, baseUrl) => String(cssText || "")
  .replace(/url\((['"]?)([^'")]+)\1\)/gi, (match, quote, url) => {
    if (!isRewritableUrl(url)) {
      return match;
    }

    return `url(${quote}${toAbsoluteUrl(url, baseUrl)}${quote})`;
  });

const rewriteLmsRelativeUrls = (rootElement, baseUrl) => {
  const urlAttributes = [
    "href",
    "src",
    "data",
    "poster",
  ];

  Array.from(rootElement.querySelectorAll("[href], [src], [data], [poster]"))
    .forEach((element) => {
      urlAttributes.forEach((attribute) => {
        if (element.hasAttribute(attribute)) {
          element.setAttribute(attribute, toAbsoluteUrl(element.getAttribute(attribute), baseUrl));
        }
      });
    });

  Array.from(rootElement.querySelectorAll("[srcset]"))
    .forEach((element) => {
      element.setAttribute("srcset", rewriteSrcSet(element.getAttribute("srcset"), baseUrl));
    });

  Array.from(rootElement.querySelectorAll("[style]"))
    .forEach((element) => {
      element.setAttribute("style", rewriteCssUrls(element.getAttribute("style"), baseUrl));
    });

  const styleElements = Array.from(rootElement.querySelectorAll("style"));
  styleElements.forEach((_, index) => {
    const styleElement = styleElements[index];
    styleElement.textContent = rewriteCssUrls(styleElement.textContent, baseUrl);
  });
};

const buildIframeDocument = ({
  html,
  staticTabUrl,
  lmsBaseUrl,
  mfeBaseUrl,
  legacyThemeName,
}) => {
  const direction = document.documentElement?.getAttribute("dir") || "ltr";
  const themePath = legacyThemeName ? `${legacyThemeName}/` : "";
  const normalizedLmsBaseUrl = removeTrailingSlash(lmsBaseUrl);
  const normalizedMfeBaseUrl = removeTrailingSlash(mfeBaseUrl || window.location.origin);

  return `
    <!doctype html>
    <html dir="${escapeHtmlAttribute(direction)}" translate="no" class="notranslate">
      <head>
        <meta charset="utf-8">
        <meta name="google" content="notranslate">
        <base href="${escapeHtmlAttribute(staticTabUrl)}">
        <link rel="stylesheet" href="${escapeHtmlAttribute(normalizedLmsBaseUrl)}/static/${escapeHtmlAttribute(themePath)}css/bootstrap/lms-main.css">
        <link rel="stylesheet" type="text/css" href="${escapeHtmlAttribute(normalizedMfeBaseUrl)}/static/LmsHtmlFragment.css">
        <style>
          body {
            margin: 0;
            padding: 0;
          }

          img {
            max-width: 100%;
            height: auto;
          }

          table {
            max-width: 100%;
            overflow-x: auto;
            display: block;
          }

          iframe {
            max-width: 100%;
          }

          .container {
            max-width: 100% !important;
            padding: 0 !important;
          }
        </style>
      </head>
      <body translate="no" class="notranslate">
        ${html}
        <script>
          (() => {
            const messageType = "${STATIC_TAB_RESIZE_MESSAGE_TYPE}";
            const minHeight = ${STATIC_TAB_MIN_HEIGHT};

            const getHeight = () => {
              const body = document.body;
              const documentElement = document.documentElement;

              return Math.max(
                (body && body.scrollHeight) || 0,
                (body && body.offsetHeight) || 0,
                (documentElement && documentElement.clientHeight) || 0,
                (documentElement && documentElement.scrollHeight) || 0,
                (documentElement && documentElement.offsetHeight) || 0,
                minHeight,
              );
            };

            const postHeight = () => {
              window.parent.postMessage({
                type: messageType,
                payload: { height: getHeight() },
              }, "*");
            };

            window.addEventListener("load", postHeight);
            window.addEventListener("resize", postHeight);
            if (window.ResizeObserver && document.body) {
              window.__staticTabResizeObserver = new ResizeObserver(postHeight);
              window.__staticTabResizeObserver.observe(document.body);
            }
            document.addEventListener("click", (event) => {
              const target = event.target;
              const link = target && target.closest ? target.closest("a[href]") : null;
              const rawHref = link && link.getAttribute("href");
              const normalizedHref = rawHref && rawHref.trim().toLowerCase();

              if (
                !link
                || !normalizedHref
                || normalizedHref.startsWith("#")
                || normalizedHref.startsWith("javascript:")
                || link.target
              ) {
                return;
              }

              event.preventDefault();
              window.parent.location.href = link.href;
            });
            postHeight();
          })();
        </script>
      </body>
    </html>
  `;
};

const StaticTab = ({ intl }) => {
  const { courseId, tabId } = useParams();
  const [iframeDocument, setIframeDocument] = useState("");
  const [iframeHeight, setIframeHeight] = useState(STATIC_TAB_MIN_HEIGHT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabTitle, setTabTitle] = useState("");
  const iframeRef = useRef(null);

  const resizeIframe = useCallback(() => {
    const iframeDocumentElement = iframeRef.current?.contentWindow?.document;
    setIframeHeight(getElementHeight(iframeDocumentElement));
  }, []);

  useEffect(() => {
    const receiveMessage = (event) => {
      if (
        event.source !== iframeRef.current?.contentWindow
        || event.data?.type !== STATIC_TAB_RESIZE_MESSAGE_TYPE
      ) {
        return;
      }

      const height = Number(event.data?.payload?.height);
      if (Number.isFinite(height)) {
        setIframeHeight(Math.max(height, STATIC_TAB_MIN_HEIGHT));
      }
    };

    global.addEventListener("message", receiveMessage);
    return () => global.removeEventListener("message", receiveMessage);
  }, []);

  useEffect(() => {
    const fetchStaticTabContent = async () => {
      try {
        setLoading(true);
        setError(null);
        setIframeDocument("");
        setIframeHeight(STATIC_TAB_MIN_HEIGHT);
        setTabTitle("");

        // Fetch the static tab content from the LMS
        const config = getConfig();
        const url = `${config.LMS_BASE_URL}/courses/${courseId}/${tabId}/`;
        const response = await getAuthenticatedHttpClient().get(url);

        // Extract the content from the HTML response
        // The LMS returns a full HTML page, we need to extract just the content
        const parser = new DOMParser();
        const doc = parser.parseFromString(response.data, "text/html");

        // Try to find the main content area
        const contentElement =
          doc.querySelector(".container") ||
          doc.querySelector(".static-tab-contents") ||
          doc.querySelector("main") ||
          doc.querySelector("body");
        const titleElement = doc.querySelector("title");
        // FAQ | 1 | SUmmac Dev
        const titleParts = titleElement?.textContent?.split(" | ");
        const displayTitle = titleParts?.[0] || "";

        if (contentElement) {
          const preparedContent = contentElement.cloneNode(true);
          const bodyScripts = Array.from(doc.querySelectorAll("body script"))
            .filter((script) => !contentElement.contains(script));

          bodyScripts.forEach((script) => {
            preparedContent.appendChild(script.cloneNode(true));
          });

          rewriteLmsRelativeUrls(preparedContent, url);

          setIframeDocument(buildIframeDocument({
            html: preparedContent.innerHTML,
            staticTabUrl: url,
            lmsBaseUrl: config.LMS_BASE_URL,
            mfeBaseUrl: config.BASE_URL,
            legacyThemeName: config.LEGACY_THEME_NAME,
          }));
          setTabTitle(displayTitle || "");
        } else {
          setIframeDocument(buildIframeDocument({
            html: response.data,
            staticTabUrl: url,
            lmsBaseUrl: config.LMS_BASE_URL,
            mfeBaseUrl: config.BASE_URL,
            legacyThemeName: config.LEGACY_THEME_NAME,
          }));
          setTabTitle(displayTitle || "");
        }
      } catch (err) {
        console.error("Error fetching static tab content:", err);
        setError(intl.formatMessage(messages.errorLoadingContent));
      } finally {
        setLoading(false);
      }
    };

    if (courseId && tabId) {
      fetchStaticTabContent();
    }
  }, [courseId, tabId, intl]);

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <Spinner
          animation="border"
          className="mie-3"
          screenReaderText={intl.formatMessage(messages.loading)}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <Alert variant="danger">{error}</Alert>
      </div>
    );
  }

  return (
    <div className="static-tab-content">
      <div role="heading" aria-level="1" className="h2 mt-3">{tabTitle}</div>
      <iframe
        allowFullScreen
        className="notranslate static-tab-iframe"
        height={iframeHeight}
        onLoad={resizeIframe}
        ref={iframeRef}
        referrerPolicy="origin"
        scrolling="no"
        srcDoc={iframeDocument}
        title={tabTitle || intl.formatMessage(messages.loading)}
        translate="no"
      />
    </div>
  );
};

StaticTab.propTypes = {
  intl: intlShape.isRequired,
};

export default injectIntl(StaticTab);
